import { isAnyOf } from '@reduxjs/toolkit';
import { persistor } from '@app/store';
import { purgeStorageAssociatedWithMode } from '@app/persist';
import { startAppListening } from '@app/listenerMiddleware';
import { AppMode, InvalidateStoreConfig, InvalidateLevel } from '@types';
import { AppSession, isValidUrl, matchesAnyRoutes, reverse } from '@utils';
import { router } from '@components/routes';
import { appActions } from './slice';
import { authActions, selectAuthToken, selectIsAuthenticated } from '../auth';
import { selectIqtModeOn } from '../user';
import { selectAppCompanyName } from './selectors';

// todo mermaid diagram
// init app
startAppListening({
    actionCreator: appActions.initApp,
    effect: async (action, { dispatch, getState, extra: { AuthService } }) => {
        const state = getState();
        const { silentLogin } = action.payload;

        // silent login flow
        if (silentLogin) {
            dispatch(authActions.silentLogin(silentLogin));
            return;
        }

        const companyName = selectAppCompanyName(state);
        const authService = AuthService.init(companyName);
        const isAuthenticated = selectIsAuthenticated(state);
        const token = selectAuthToken(state);

        // short iqt flow
        if (AppSession.shortIqtModeOn && !isAuthenticated) {
            dispatch(authActions.obtainSessionToken());
            return;
        }

        // sso flow
        const ssoToken = authService.getSsoToken();

        if (ssoToken) {
            authService.setHeaders(ssoToken);
            authService.clearSsoToken();
            dispatch(authActions.ssoLoginSuccess({ token: ssoToken }));
            return;
        }

        // default flow
        if (token) {
            authService.setHeaders(token);
            dispatch(appActions.fetchSettings());
            return;
        } else {
            dispatch(authActions.obtainAnonymousToken());
            return;
        }
    },
});

// init app on getting token
startAppListening({
    matcher: isAnyOf(
        authActions.obtainAnonymousTokenSuccess,
        authActions.obtainSessionTokenSuccess,
        authActions.silentLoginSuccess,
        authActions.ssoLoginSuccess,
    ),
    effect: async (action, { dispatch }) => {
        dispatch(appActions.fetchSettings());
    },
});

// invalidate app
startAppListening({
    actionCreator: appActions.invalidateStore,
    effect: async (action, { dispatch, getState }) => {
        const state = getState();
        const iqtModeOn = selectIqtModeOn(state);
        const { redirect, redirectByRouter, purge } = action.payload;

        const fullClean = purge === InvalidateLevel.Full;

        function goNext(redirect: InvalidateStoreConfig['redirect']) {
            if (!redirect) return;

            let url;

            if (matchesAnyRoutes(redirect) || isValidUrl(redirect)) {
                url = redirect;
            } else {
                url = reverse('widgetUpload');
            }

            if (redirectByRouter) {
                router.navigate(redirect, { replace: true });
            } else {
                window.location.href = url;
            }
        }

        if (fullClean) {
            // first clear the opposite mode and then the current one
            purgeStorageAssociatedWithMode(iqtModeOn ? AppMode.EndUser : AppMode.Iqt).then(() => {
                persistor.purge().then(() => {
                    goNext(redirect);
                });
            });
        } else {
            goNext(redirect);
        }
    },
});
